import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ProfileProvider } from './contexts/ProfileContext';
import ProtectedRoute from './components/ProtectedRoute';
import Landing from './pages/Landing';
import AuthRedirect from './pages/AuthRedirect';
import Onboarding from './pages/Onboarding';
import CalculatingChart from './pages/CalculatingChart';
import PersonalitySummary from './pages/PersonalitySummary';
import Profile from './pages/Profile';
import History from './pages/History';
import ReadingReplay from './pages/ReadingReplay';
import Settings from './pages/Settings';
import Regenerating from './pages/Regenerating';
import Home from './pages/Home';
import Reading from './pages/Reading';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsAndConditions from './pages/TermsAndConditions';
import TabLayout from './layout/TabLayout';
import logger from './utils/logger';
import './App.css';

function AppRoutes() {
  const location = useLocation();

  useEffect(() => {
    logger.componentMount('App', { pathname: location.pathname });
    logger.info('App', 'Route changed', { pathname: location.pathname, search: location.search });
    return () => {
      logger.componentUnmount('App');
    };
  }, [location.pathname, location.search]);

  return (
    <div className="App">
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
        <Route path="/auth-redirect" element={<AuthRedirect />} />
        <Route path="/onboarding" element={
          <ProtectedRoute>
            <Onboarding />
          </ProtectedRoute>
        } />
        <Route path="/calculating-chart" element={
          <ProtectedRoute>
            <CalculatingChart />
          </ProtectedRoute>
        } />
        <Route path="/user" element={
          <ProtectedRoute requireOnboarding>
            <TabLayout>
              <Profile />
            </TabLayout>
          </ProtectedRoute>
        } />
        <Route path="/profile/history" element={
          <ProtectedRoute requireOnboarding>
            <TabLayout>
              <History />
            </TabLayout>
          </ProtectedRoute>
        } />
        <Route path="/reading/view/:id" element={
          <ProtectedRoute requireOnboarding>
            <TabLayout>
              <ReadingReplay />
            </TabLayout>
          </ProtectedRoute>
        } />
        <Route path="/profile/settings" element={
          <ProtectedRoute requireOnboarding>
            <TabLayout>
              <Settings />
            </TabLayout>
          </ProtectedRoute>
        } />
        <Route path="/regenerating" element={
          <ProtectedRoute requireOnboarding>
            <TabLayout>
              <Regenerating />
            </TabLayout>
          </ProtectedRoute>
        } />
        <Route path="/personality" element={
          <ProtectedRoute requireOnboarding>
            <TabLayout>
              <PersonalitySummary />
            </TabLayout>
          </ProtectedRoute>
        } />
        <Route path="/home" element={
          <ProtectedRoute requireOnboarding>
            <TabLayout>
              <Home />
            </TabLayout>
          </ProtectedRoute>
        } />
        <Route path="/reading/daily" element={
          <ProtectedRoute requireOnboarding>
            <TabLayout>
              <Reading />
            </TabLayout>
          </ProtectedRoute>
        } />
        <Route path="/reading" element={
          <ProtectedRoute requireOnboarding>
            <TabLayout>
              <Reading />
            </TabLayout>
          </ProtectedRoute>
        } />
        {/* Additional routes will be added in later phases */}
      </Routes>
    </div>
  );
}

function App() {
  useEffect(() => {
    logger.info('App', 'Application initializing', {
      env: process.env.NODE_ENV,
      apiUrl: process.env.REACT_APP_API_URL || 'http://localhost:8000',
      loggingEnabled: process.env.REACT_APP_ENABLE_LOGGING === 'true'
    });
  }, []);

  return (
    <Router>
      <AuthProvider>
        <ProfileProvider>
          <AppRoutes />
        </ProfileProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
